import { useState, useEffect } from "react";
import "../../../../../../styles/selectBar.css";
import ReviewSettingsPanel from "../../../../../../components/ReviewSettingsPanel";

/**
 * ── 변경사항 (ECHO 톤) ──
 * 1. 복습 설정 재설계:
 *    - "복습이란?" 플로우 안내 (첫 풀이 → 틀린 것만 → 완료)
 *    - 자동/수동 복습 차이를 카드로 설명
 *    - 최대 횟수 = 세그먼트 칩 (1·2·3·5·10·무제한) + 무제한 경고
 * 2. "제한 없음" → "무제한"으로 통일 (값 -1 그대로)
 * 3. 유형별 문제 수: 0개 유형은 흐리게, 있는 유형 강조
 * 4. selectedTypes 데이터 구조({key:"exam", count, isRetryEnabled,
 *    isAutoRetryEnabled, maxRetries})와 쿠키 저장 로직은 기존 그대로
 */
const VocabularySelectionPage = ({
  worksheet,
  selectedTypes,
  setSelectedTypes,
}) => {
  const [isRetryEnabled, setIsRetryEnabled] = useState(() => {
    const found = selectedTypes.find((type) => type.key === "exam");
    return found ? found.isRetryEnabled !== false : true;
  });
  const [isAutoRetryEnabled, setIsAutoRetryEnabled] = useState(false);
  const [maxRetries, setMaxRetries] = useState(() => {
    const found = selectedTypes.find((type) => type.key === "exam");
    if (found && typeof found.maxRetries === "number") return found.maxRetries;
    const cookieValue = (() => {
      const match = document.cookie.match(/(?:^|; )maxRetries=([^;]*)/);
      return match ? decodeURIComponent(match[1]) : undefined;
    })();
    if (cookieValue !== undefined) return Number(cookieValue);
    return 3;
  });

  const examData =
    setSelectedTypes === undefined
      ? []
      : worksheet.data.find((paragraph) => paragraph.title.includes("테스트"))
          .data;

  const maxExamCount = setSelectedTypes === undefined ? 99 : examData.length;

  const examCount =
    selectedTypes.find((type) => type.key === "exam")?.count || 0;

  const commit = (patch) => {
    const next = {
      key: "exam",
      count: examCount,
      isRetryEnabled,
      isAutoRetryEnabled,
      maxRetries: isRetryEnabled ? maxRetries : 0,
      ...patch,
    };
    setSelectedTypes([next]);
  };

  const setExamCount = (count) => commit({ count });

  const handleRetryToggle = () => {
    const enabled = !isRetryEnabled;
    setIsRetryEnabled(enabled);
    commit({
      isRetryEnabled: enabled,
      isAutoRetryEnabled: enabled ? isAutoRetryEnabled : false,
      maxRetries: enabled ? maxRetries : 0,
    });
  };

  const handleAutoRetry = (enabled) => {
    setIsAutoRetryEnabled(enabled);
    commit({ isAutoRetryEnabled: enabled });
  };

  const handleMaxRetries = (value) => {
    setMaxRetries(value);
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `maxRetries=${encodeURIComponent(
      value
    )}; expires=${expires.toUTCString()}; path=/`;
    commit({ maxRetries: isRetryEnabled ? value : 0 });
  };

  useEffect(() => {
    if (setSelectedTypes !== undefined) {
      setExamCount(maxExamCount);
    }
    // eslint-disable-next-line
  }, [maxExamCount]);

  const examTypeList = [
    { type: "ant", text: "반의어 → 영단어 적기 (주관식)" },
    { type: "syn", text: "동의어 → 영단어 적기 (주관식)" },
    { type: "engeng", text: "영어 뜻풀이 → 영단어 적기 (주관식)" },
    { type: "engkor", text: "영단어 → 한국어 뜻 적기 (주관식)" },
    { type: "koreng", text: "한국어 뜻 → 영단어 적기 (주관식)" },
    { type: "example_blank", text: "한국어 뜻 → 빈칸 채우기 (주관식)" },
    { type: "ant_choice", text: "반의어 → 영단어 적기 (객관식)" },
    { type: "syn_choice", text: "동의어 → 영단어 적기 (객관식)" },
    { type: "engeng_choice", text: "영어 뜻풀이 → 영단어 적기 (객관식)" },
    { type: "engkor_choice", text: "영단어 → 한국어 뜻 적기 (객관식)" },
    { type: "koreng_choice", text: "한국어 뜻 → 영단어 적기 (객관식)" },
    { type: "example_blank_choice", text: "한국어 뜻 → 빈칸 채우기 (객관식)" },
    // 지인에듀 단어 3판(2026-11)
    { type: "collocation_blank_choice", text: "짝 표현 빈칸 채우기 (객관식)" },
    { type: "collocation_odd_choice", text: "어색한 짝 표현 고르기 (객관식)" },
    { type: "collocation_meaning_choice", text: "짝 표현 → 한국어 뜻 고르기 (객관식)" },
    { type: "context_blank_choice", text: "한국어 해석 → 문장 빈칸 채우기 (객관식)" },
    { type: "collocation_blank", text: "짝 표현 빈칸 채우기 (주관식)" },
    { type: "collocation_write", text: "한국어 뜻 → 짝 표현 적기 (주관식)" },
  ];

  if (examData.length > 0) {
    for (let typeData of examTypeList) {
      typeData.count = examData[0].data.filter(
        (item) => item.type === typeData.type
      ).length;
    }
  }

  return (
    <div style={{ padding: "22px" }}>
      <div className="ws-modal-title">테스트 세트 설정</div>
      <p className="ws-modal-sub" style={{ marginBottom: "14px" }}>
        출제할 세트 수와 복습 방식을 정합니다.
      </p>

      {/* 세트 수 */}
      <div style={sectionLabel}>세트 수</div>
      <select
        value={examCount}
        onChange={(e) => setExamCount(Number(e.target.value))}
        disabled={setSelectedTypes === undefined}
        className="customSelect"
        style={{
          width: "130px",
          fontSize: "0.9rem",
          padding: "9px 13px",
          border: "1.5px solid #E6EDEC",
          borderRadius: "10px",
          fontFamily: "inherit",
          backgroundColor: "#fff",
          cursor: "pointer",
          marginBottom: "18px",
        }}
      >
        {[...Array(maxExamCount)].map((_, index) => (
          <option key={index + 1} value={index + 1}>
            {index + 1}개 세트
          </option>
        ))}
      </select>

      {/* ── 복습 설정 (공용 패널) ── */}
      <div style={{ marginBottom: "18px" }}>
        <ReviewSettingsPanel
          isRetryEnabled={isRetryEnabled}
          onToggleRetry={handleRetryToggle}
          isAutoRetryEnabled={isAutoRetryEnabled}
          onSetAutoRetry={handleAutoRetry}
          maxRetries={maxRetries}
          onSetMaxRetries={handleMaxRetries}
        />
      </div>

      {/* 유형별 문제 수 */}
      {setSelectedTypes !== undefined && (
        <>
          <div style={sectionLabel}>이 세트에 담긴 문제</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "3px",
            }}
          >
            {examTypeList
              .slice()
              .sort((a, b) => (b.count || 0) - (a.count || 0))
              .map((typeData) => {
                const has = (typeData.count || 0) > 0;
                return (
                  <div
                    key={typeData.type}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      padding: "5px 2px",
                      color: has ? "#0E141B" : "#C6CDD4",
                      fontWeight: has ? 700 : 500,
                    }}
                  >
                    <span>{typeData.text}</span>
                    <span
                      style={{
                        fontVariantNumeric: "tabular-nums",
                        color: has ? "#00A396" : "#C6CDD4",
                        fontWeight: 800,
                      }}
                    >
                      {typeData.count || 0}개
                    </span>
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

const sectionLabel = {
  fontSize: "0.72rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  color: "#7A8490",
  marginBottom: "7px",
};

export default VocabularySelectionPage;
